package com.dotdrives.model

import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.minus
import kotlinx.datetime.plus
import kotlinx.datetime.daysUntil

data class ReportPeriod(val start: LocalDate, val endInclusive: LocalDate, val label: String) {
    init { require(endInclusive >= start) }
}

enum class ReportPeriodKind { FiscalYear, RollingTwelve, Custom }

data class ReportFilter(
    val period: ReportPeriod,
    val kind: ReportPeriodKind,
    val targetId: String? = null,
    val stewardUserId: String? = null,
)

data class GoalProgress(
    val raised: Double,
    val goal: Double?,
    val percent: Double?,
    val paceAmount: Double?,
    val paceDifference: Double?,
    val daysRemaining: Int?,
)

fun fiscalYearPeriod(fiscalYear: Int, startMonth: Int, startDay: Int): ReportPeriod {
    require(startMonth in 1..12 && startDay in 1..28)
    val start = LocalDate(if (startMonth == 1 && startDay == 1) fiscalYear else fiscalYear - 1, startMonth, startDay)
    val end = start.plus(DatePeriod(years = 1)).minus(DatePeriod(days = 1))
    return ReportPeriod(start, end, "FY $fiscalYear")
}

fun rollingTwelveMonths(asOf: LocalDate): ReportPeriod =
    ReportPeriod(asOf.minus(DatePeriod(years = 1)).plus(DatePeriod(days = 1)), asOf, "Last 12 months")

fun customReportPeriod(start: LocalDate, endInclusive: LocalDate): ReportPeriod =
    ReportPeriod(start, endInclusive, "$start – $endInclusive")

fun sameDatePriorYear(date: LocalDate): LocalDate =
    if (date.monthNumber == 2 && date.dayOfMonth == 29) LocalDate(date.year - 1, 2, 28)
    else LocalDate(date.year - 1, date.monthNumber, date.dayOfMonth)

fun inclusiveDayCount(start: LocalDate, endInclusive: LocalDate): Int = start.daysUntil(endInclusive) + 1

fun goalProgress(
    raised: Double,
    goal: Double?,
    start: LocalDate?,
    end: LocalDate?,
    asOf: LocalDate,
): GoalProgress {
    val usableGoal = goal?.takeIf { it > 0.0 }
    val percent = usableGoal?.let { raised / it * 100.0 }
    val pace = if (usableGoal != null && start != null && end != null && end >= start && asOf < start) {
        0.0
    } else if (usableGoal != null && start != null && end != null && end >= start) {
        val elapsedEnd = minOf(maxOf(asOf, start), end)
        usableGoal * inclusiveDayCount(start, elapsedEnd) / inclusiveDayCount(start, end).toDouble()
    } else null
    return GoalProgress(
        raised = raised,
        goal = goal,
        percent = percent,
        paceAmount = pace,
        paceDifference = pace?.let { raised - it },
        daysRemaining = if (end != null) maxOf(0, asOf.daysUntil(end)) else null,
    )
}

fun goalWindowProgress(raised:Double,goal:Double?,goalStart:LocalDate?,goalEnd:LocalDate?,windowStart:LocalDate,asOf:LocalDate):GoalProgress {
    val atEnd=goalProgress(raised,goal,goalStart,goalEnd,asOf)
    val beforeWindow=goalProgress(0.0,goal,goalStart,goalEnd,windowStart.minus(DatePeriod(days=1))).paceAmount ?: return atEnd
    val expected=maxOf(0.0,(atEnd.paceAmount?:0.0)-beforeWindow)
    return atEnd.copy(paceAmount=expected,paceDifference=raised-expected)
}
